import { inject, Injectable } from "@angular/core";
import { WA_WINDOW } from "@ng-web-apis/common";

const INSTANCE_ID_KEY = "admefy-player-instance-id";
const INSTANCE_CHANNEL = "admefy-player-instance-coordination";

@Injectable({ providedIn: "root" })
export class PlayerInstanceIdentityService {
  private readonly window = inject(WA_WINDOW);
  private instanceId = this.getOrCreateInstanceId();
  readonly ready = this.ensureUniqueTabIdentity();

  get value(): string {
    return this.instanceId;
  }

  private getOrCreateInstanceId(): string {
    try {
      const stored = this.window.sessionStorage.getItem(INSTANCE_ID_KEY);
      if (stored) return stored;
    } catch {
      // Some embedded browsers disable session storage; retain an in-memory identity.
    }
    const instanceId = this.createInstanceId();
    this.persist(instanceId);
    return instanceId;
  }

  private ensureUniqueTabIdentity(): Promise<void> {
    const BroadcastChannelConstructor = globalThis.BroadcastChannel;
    if (typeof BroadcastChannelConstructor !== "function") return Promise.resolve();
    const channel = new BroadcastChannelConstructor(INSTANCE_CHANNEL);
    const nonce = this.createInstanceId();
    let probing = true;
    let collision = false;
    channel.addEventListener("message", (event: MessageEvent<unknown>) => {
      const message = event.data as { type?: string; instanceId?: string; nonce?: string };
      if (message.instanceId !== this.instanceId || message.nonce === nonce) return;
      if (message.type === "probe") {
        channel.postMessage({ type: "present", instanceId: this.instanceId, nonce });
        if (probing) collision = true;
      } else if (message.type === "present" && probing) {
        collision = true;
      }
    });
    channel.postMessage({ type: "probe", instanceId: this.instanceId, nonce });
    return new Promise((resolve) => {
      this.window.setTimeout(() => {
        if (collision) {
          this.instanceId = this.createInstanceId();
          this.persist(this.instanceId);
        }
        probing = false;
        channel.postMessage({ type: "present", instanceId: this.instanceId, nonce });
        resolve();
      }, 100);
    });
  }

  private persist(instanceId: string): void {
    try {
      this.window.sessionStorage.setItem(INSTANCE_ID_KEY, instanceId);
    } catch {
      // The service lifetime still keeps this tab identity stable.
    }
  }

  private createInstanceId(): string {
    if (typeof this.window.crypto.randomUUID === "function") return this.window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    this.window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
